import { useEffect, useRef, useState } from 'react';
import { Accordion } from '@mui/joy';
import type { AccordionProps } from '@mui/joy';

export type TabbableAccordionProps = Omit<AccordionProps, 'onChange'> & {
  /** Called whenever the user opens or closes the section. */
  onExpandedChange?: (expanded: boolean) => void;
};

/**
 * A Joy UI `Accordion` whose fields are still reachable with Tab after the
 * section has been collapsed and reopened.
 *
 * `@mui/joy@5.0.0-beta.52`'s `AccordionDetails` keeps collapsed content out of
 * the tab order by setting `tabindex="-1"` on every focusable descendant and
 * recording the old value in `data-prev-tabindex`. Its restore path is:
 *
 * ```js
 * if (currentTabIndex && prevTabIndex) { ...restore... }
 * if (!prevTabIndex && !currentTabIndex) { ...remove tabindex... }
 * ```
 *
 * A plain `<input>`/`<button>` has no `tabindex` to begin with, so the saved
 * value is `''`. On reopen that makes `prevTabIndex` falsy and `currentTabIndex`
 * `'-1'` (truthy), so neither branch runs and the field is left at
 * `tabindex="-1"` permanently. Tab then skips the whole section's fields and
 * moves to the next section's summary button.
 *
 * Effects run child-first, so the effect here fires after `AccordionDetails`
 * has done its bookkeeping and can undo it. Only expanded sections are
 * repaired — collapsed ones are meant to stay untabbable.
 *
 * Note that this only bites elements with no `tabindex` of their own — a plain
 * `<a>`, `<button>`, `<input>`, `<textarea>` or `<select>`. Anything carrying an
 * explicit `tabindex` (Joy's `ListItemButton` renders `tabindex="0"`) records a
 * truthy previous value and is restored correctly by Joy itself.
 *
 * Works either uncontrolled (`defaultExpanded`) or controlled (`expanded`,
 * paired with `onExpandedChange`), mirroring Joy's own `Accordion`.
 */
export const TabbableAccordion = ({
  defaultExpanded = false,
  expanded: expandedProp,
  onExpandedChange,
  children,
  ...props
}: TabbableAccordionProps) => {
  const [uncontrolledExpanded, setUncontrolledExpanded] = useState(defaultExpanded);
  const rootRef = useRef<HTMLDivElement>(null);

  const isControlled = expandedProp !== undefined;
  const expanded = isControlled ? expandedProp : uncontrolledExpanded;

  useEffect(() => {
    if (!expanded) return;
    const root = rootRef.current;
    if (!root) return;

    root.querySelectorAll<HTMLElement>('[data-prev-tabindex]').forEach((element) => {
      const previous = element.getAttribute('data-prev-tabindex');
      element.removeAttribute('data-prev-tabindex');
      if (previous) {
        element.setAttribute('tabindex', previous);
      } else {
        element.removeAttribute('tabindex');
      }
    });
  }, [expanded]);

  return (
    <Accordion
      {...props}
      ref={rootRef}
      expanded={expanded}
      onChange={(_, isExpanded) => {
        if (!isControlled) {
          setUncontrolledExpanded(isExpanded);
        }
        onExpandedChange?.(isExpanded);
      }}
    >
      {children}
    </Accordion>
  );
};

export default TabbableAccordion;
